/**
 * Landing page — public marketing entry point at `/`.
 *
 * Style matches the Cybernet 360 portfolio (Partner360, CRM360, Flow360):
 * dark navy `#0B1121` background + teal accents + Framer Motion animations
 * + lucide-react icons. Eager-imported (NOT lazy) per Lesson 41 — first
 * paint matters most for unauthenticated visitors.
 *
 * Routes
 * ------
 *   /        → this page (unauthenticated)
 *   /login   → LoginPage
 *   /app     → DashboardPage (authenticated)
 *
 * If a signed-in user lands on `/`, the auth context will detect their
 * session and the App router shows them the Dashboard instead. We don't
 * redirect here — that's handled in App.tsx so SSR-style first paint stays
 * intact.
 */

import { useEffect, useState } from "react";
import { Link } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity,
  ArrowRight,
  Brain,
  CheckCircle2,
  Eye,
  Globe,
  Languages,
  Lock,
  Radio,
  Shield,
  Siren,
  WifiOff,
} from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";

// ── Hero rotating word ─────────────────────────────────────────────────────
const ROTATING_WORDS = ["Pipelines", "Rail Lines", "Power Cables", "Borders"];

function RotatingHeadline() {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % ROTATING_WORDS.length), 2400);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="inline-block align-top relative h-[1.1em] min-w-[260px] overflow-hidden lg:min-w-[480px]">
      <AnimatePresence mode="wait">
        <motion.span
          key={index}
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -40, opacity: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="absolute left-0 top-0 block bg-gradient-to-r from-teal-300 to-teal-500 bg-clip-text text-transparent"
        >
          {ROTATING_WORDS[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

// ── Stagger animation helpers ─────────────────────────────────────────────
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } },
};

// ── 6 feature cards (PRD §5 layers) ───────────────────────────────────────
const FEATURES = [
  {
    icon: Radio,
    iconClass: "text-teal-300 bg-teal-500/10",
    title: "Real-time DAS Ingest",
    desc: "Normalised events from any DAS interrogator vendor. TimescaleDB hypertable + 7-day compression. <500 ms p95 ingest, zero event loss on outage.",
  },
  {
    icon: Brain,
    iconClass: "text-violet-300 bg-violet-500/10",
    title: "Hybrid AI Classification",
    desc: "scikit-learn baseline (92.1% accuracy) + Anthropic Claude reasoning grounded in operational context — work permits, train schedule, weather, segment baseline.",
  },
  {
    icon: Eye,
    iconClass: "text-amber-300 bg-amber-500/10",
    title: "Human-in-the-Loop Console",
    desc: "Every alert reviewed by a human before dispatch. AI explains its reasoning. Operators confirm in seconds with full evidence panel and signal waveform.",
  },
  {
    icon: Siren,
    iconClass: "text-rose-300 bg-rose-500/10",
    title: "Telegram Field Dispatch",
    desc: "Per-site recipient groups. MarkdownV2 escaped templates. 3-retry queue with exponential backoff. Zero third-party gateway lock-in.",
  },
  {
    icon: Lock,
    iconClass: "text-emerald-300 bg-emerald-500/10",
    title: "Append-Only Audit Trail",
    desc: "PostgreSQL triggers reject UPDATE/DELETE on the audit log for ALL roles, including superuser. PETRONAS-compliant. Immutable forever.",
  },
  {
    icon: WifiOff,
    iconClass: "text-blue-300 bg-blue-500/10",
    title: "Edge-First Architecture",
    desc: "Core detection runs locally on industrial PC. pg-boss store-and-forward queue. Drains on reconnect. Designed for remote rail and pipeline corridors.",
  },
];

// ── 4 benefit cards ────────────────────────────────────────────────────────
const BENEFITS = [
  {
    icon: Shield,
    title: "CSPS-Aligned",
    desc: "Built to the Cybernet SaaS Product Standard v1.0 — RBAC, CSRF, rate-limit, append-only audit, session.regenerate. 36/40 enterprise score.",
  },
  {
    icon: Languages,
    title: "Bilingual BM/EN",
    desc: "Full Bahasa Melayu and English — operator UI, AI reasoning text, audit log notes, error messages. PETRONAS-ready out of the box.",
  },
  {
    icon: CheckCircle2,
    title: "Trustworthy AI",
    desc: "Every Claude inference logged with model version, token count, latency, and reasoning. trustLayer.ts wrapper. Falls back to rule-based if API unavailable.",
  },
  {
    icon: Activity,
    title: "Proven Stack",
    desc: "Same Node 20 + Express 5 + Drizzle + PostgreSQL stack as 15 production Cybernet 360 apps. Same ops playbook. No experimental dependencies.",
  },
];

// ── Stats strip ────────────────────────────────────────────────────────────
const STATS = [
  { value: "92.1%", label: "Classifier accuracy" },
  { value: "<5s", label: "End-to-end alert latency" },
  { value: "100%", label: "Audit trail coverage" },
  { value: "0", label: "Event loss on outage" },
];

export function LandingPage() {
  const { t } = useTranslation();
  void t; // i18n applied to header/footer copy below

  return (
    <div className="min-h-screen bg-[#0B1121] text-slate-100" data-testid="landing-page">
      {/* ── Top nav ─────────────────────────────────────────────────────── */}
      <header className="border-b border-slate-800/60 backdrop-blur sticky top-0 z-20 bg-[#0B1121]/85">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="text-teal-400 size-5" />
            <span className="font-semibold tracking-tight">
              <span className="text-teal-400">Ventura</span>{" "}
              <span className="text-slate-200">Sentinel</span>
            </span>
            <span className="hidden sm:inline text-[10px] text-slate-500 uppercase tracking-widest border border-slate-700 rounded px-1.5 py-0.5 ml-2">
              by Cybernet
            </span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link
              href="/login"
              data-testid="landing-login-cta"
              className="text-xs font-semibold text-slate-900 bg-teal-400 hover:bg-teal-300 transition-colors rounded-md px-3 py-1.5 inline-flex items-center gap-1"
            >
              Sign in <ArrowRight className="size-3" />
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-20%,rgba(20,184,166,0.18),transparent_60%)] pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 py-20 sm:py-28 relative">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-4xl space-y-6"
          >
            <motion.div
              variants={fadeInUp}
              className="inline-flex items-center gap-2 text-xs text-teal-300 border border-teal-800/60 bg-teal-500/5 rounded-full px-3 py-1"
            >
              <Activity className="size-3" /> AI-Powered DAS Operations · Pilot for PETRONAS GAS Berhad
            </motion.div>
            <motion.h1
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight text-slate-50"
              data-testid="landing-hero-headline"
            >
              Stop drowning in DAS alerts.
              <br />
              Start trusting <RotatingHeadline />.
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-slate-400 max-w-2xl leading-relaxed"
            >
              Ventura Sentinel turns thousands of fibre-optic acoustic events into a handful of
              human-confirmed incidents — with full AI reasoning, immutable audit, and Telegram
              dispatch to your field teams. Built by Cybernet for Ventura IOT.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-3 pt-2">
              <Link
                href="/login"
                data-testid="landing-cta-primary"
                className="inline-flex items-center gap-2 bg-teal-400 hover:bg-teal-300 text-slate-900 font-semibold px-5 py-3 rounded-md transition-colors"
              >
                Open the Console <ArrowRight className="size-4" />
              </Link>
              <a
                href="#features"
                className="inline-flex items-center gap-2 text-slate-300 border border-slate-700 hover:border-slate-500 px-5 py-3 rounded-md transition-colors"
              >
                See how it works
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Stats strip ────────────────────────────────────────────────── */}
      <section className="border-y border-slate-800/60 bg-slate-900/30">
        <div className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-2 sm:grid-cols-4 gap-6">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.4 }}
              className="text-center"
              data-testid={`stat-${i}`}
            >
              <div className="text-3xl sm:text-4xl font-bold text-teal-300 font-mono">{s.value}</div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Features grid (6 cards) ────────────────────────────────────── */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <h2 className="text-3xl font-bold text-slate-100" data-testid="landing-features-heading">
            Eight system layers. One operator screen.
          </h2>
          <p className="text-slate-400 mt-3">
            From the interrogator at the edge to the field team's Telegram channel — Sentinel handles
            every step in between.
          </p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="features-grid">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: (i % 3) * 0.08, duration: 0.4 }}
              className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 hover:border-teal-700/60 transition-colors"
            >
              <div className={`inline-flex items-center justify-center size-10 rounded-lg ${f.iconClass} mb-3`}>
                <f.icon className="size-5" />
              </div>
              <h3 className="font-semibold text-slate-100">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed mt-2">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Benefits (4 cards) ─────────────────────────────────────────── */}
      <section className="border-t border-slate-800/60 bg-slate-900/20 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-2xl mx-auto mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-100">
              Built for critical infrastructure operators
            </h2>
            <p className="text-slate-400 mt-3">
              Not a generic AI chatbot. Not a research project. Sentinel is the same engineering
              standard that runs 15 production Cybernet 360 applications across government and
              utilities.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="benefits-grid">
            {BENEFITS.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                className="rounded-xl border border-slate-800 bg-[#0B1121]/60 p-5"
              >
                <b.icon className="size-6 text-teal-400 mb-3" />
                <h3 className="font-semibold text-slate-100">{b.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed mt-2">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA strip ──────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="rounded-2xl border border-teal-900/60 bg-gradient-to-br from-teal-950/40 to-slate-900/60 p-8 sm:p-12 text-center space-y-4">
          <Globe className="size-10 text-teal-300 mx-auto" />
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-50">
            Ready to retire your alert fatigue?
          </h2>
          <p className="text-slate-400 max-w-xl mx-auto">
            Sentinel runs as a milestone-based pilot — no upfront payment, IP transfers to Ventura
            on M2 sign-off. If we don't hit 30% false-alarm reduction, you keep everything and we
            absorb the cost.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <Link
              href="/login"
              data-testid="landing-cta-secondary"
              className="inline-flex items-center gap-2 bg-teal-400 hover:bg-teal-300 text-slate-900 font-semibold px-5 py-3 rounded-md"
            >
              Sign in to the console <ArrowRight className="size-4" />
            </Link>
            <a
              href="mailto:tejo@cybernet.com.my"
              className="inline-flex items-center gap-2 text-slate-300 border border-slate-700 hover:border-slate-500 px-5 py-3 rounded-md"
            >
              Talk to Cybernet
            </a>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-800/60 bg-[#0B1121]">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>
            <span className="text-slate-400 font-medium">Cybernet Consulting Sdn Bhd</span>
            {" · "}
            202501039024 (1640433-T)
          </div>
          <div className="flex items-center gap-4">
            <a href="https://www.cybernet.com.my" className="hover:text-slate-300">
              cybernet.com.my
            </a>
            <a href="mailto:tejo@cybernet.com.my" className="hover:text-slate-300">
              tejo@cybernet.com.my
            </a>
          </div>
        </div>
      </footer>

    </div>
  );
}
